import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Sparkles, Check } from "lucide-react";

export const eur = (n) => new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(n || 0);
const PLAN_LABEL = { "7d": "fa.days7", "30d": "fa.days30", "90d": "fa.months3" };
const BOOST_LABEL = { "24h": "fa.hours24", "3d": "fa.days3", "7d": "fa.days7", "30d": "fa.days30" };

const Choice = ({ active, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`relative rounded-xl border-2 px-3 py-3 text-left transition-colors ${active ? "border-findo-red bg-findo-red/5" : "border-border hover:border-findo-navy/40"}`}>
    {active && <Check size={14} className="absolute top-2 right-2 text-findo-red" />}
    {children}
  </button>
);

export default function PublicationPlanPicker({ plan, boost, onChange, country, discount = 0, title }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    api(`/public-ads/publication/plans?country=${encodeURIComponent(country || "")}`, { auth: false }).then((d) => {
      setData(d);
      if (boost && !d.boosts.some((x) => x.key === boost)) onChange({ plan, boost: null });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);
  if (!data) return null;
  const free = data.free_publication;
  const planPrice = (x) => (free ? 0 : Math.floor(x.ttc * (1 - discount) * 100 + 1e-6) / 100);
  const p = data.plans.find((x) => x.key === plan);
  const b = data.boosts.find((x) => x.key === boost);
  const total = (p ? planPrice(p) : 0) + (b?.price_eur || 0);
  return (
    <div className="space-y-4 rounded-2xl border border-findo-navy/15 bg-slate-50/60 dark:bg-white/[0.04] dark:border-white/10 p-4" data-testid="publication-plan-picker">
      <div>
        {free && (
          <div className="mb-3 rounded-xl bg-emerald-600 text-white px-3.5 py-2.5 text-sm font-semibold" data-testid="africa-free-banner">
            {t("fa.africaFree")}
          </div>
        )}
        <p className="text-sm font-semibold text-findo-navy">{title || t("fa.planTitle")} <span className="text-findo-red">*</span></p>
        <p className="text-xs text-slate-500 mt-0.5">{free ? t("fa.planSubFree") : discount ? t("fa.renewDiscountHint") : t("fa.planSub")}</p>
        <div className="mt-2.5 grid grid-cols-3 gap-2">
          {data.plans.map((x) => (
            <Choice key={x.key} active={plan === x.key} onClick={() => onChange({ plan: x.key, boost })} testid={`plan-${x.key}`}>
              <p className="text-sm font-bold text-findo-navy">{t(PLAN_LABEL[x.key])}</p>
              {free ? (
                <p className="text-base font-extrabold text-emerald-600 mt-1">{t("fa.free")}</p>
              ) : (
                <>
                  {discount > 0 && <p className="text-[11px] text-slate-400 line-through">{eur(x.ttc)}</p>}
                  <p className="text-base font-extrabold text-slate-900 mt-1">{eur(planPrice(x))}</p>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">{t("fa.ttc")}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{eur(Math.round(planPrice(x) / 1.2 * 100) / 100)} {t("fa.ht")}</p>
                </>
              )}
            </Choice>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold text-findo-navy inline-flex items-center gap-1.5"><Sparkles size={14} className="text-findo-red" /> {t("fa.boostTitle")}</p>
        <p className="text-xs text-slate-500 mt-0.5">{t("fa.boostSub")}</p>
        <div className="mt-2.5 grid grid-cols-3 sm:grid-cols-5 gap-1.5">
          <Choice active={!boost} onClick={() => onChange({ plan, boost: null })} testid="boost-none">
            <p className="text-xs font-semibold text-slate-600">{t("fa.noBoost")}</p>
          </Choice>
          {data.boosts.map((x) => (
            <Choice key={x.key} active={boost === x.key} onClick={() => onChange({ plan, boost: x.key })} testid={`boost-${x.key}`}>
              <p className="text-[11px] leading-tight font-semibold text-findo-navy hyphens-auto break-words">{t(BOOST_LABEL[x.key])}</p>
              <p className="text-sm font-bold text-slate-900">{eur(x.price_eur)}</p>
            </Choice>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="text-sm font-semibold text-slate-600">{t("fa.total")}</span>
        <span className="text-lg font-extrabold text-findo-navy" data-testid="publication-total">{eur(total)} {t("fa.ttc")}</span>
      </div>
      {!title && <p className="text-[11px] text-slate-500" data-testid="publication-public-note">{t("fa.publicNote")}</p>}
    </div>
  );
}
